import { useState, KeyboardEvent } from 'react';
import { X } from 'lucide-react';

interface RouteTagsInputProps {
  label: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  hint?: string;
  labelClass: string;
}

export function RouteTagsInput({
  label,
  tags,
  onChange,
  placeholder = 'e.g., Accra - Tema',
  hint,
  labelClass,
}: RouteTagsInputProps) {
  const [draft, setDraft] = useState('');

  const addTag = (raw: string) => {
    const value = raw.trim().replace(/\s+/g, ' ');
    if (!value) return;
    const key = value.toLowerCase();
    if (tags.some((t) => t.toLowerCase() === key)) {
      setDraft('');
      return;
    }
    onChange([...tags, value]);
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(draft);
    } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex flex-wrap gap-1.5 p-2 min-h-[42px] rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 focus-within:ring-2 focus-within:ring-primary-500/40">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 text-xs font-medium"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange(tags.filter((t) => t !== tag))}
              className="hover:text-emerald-950 dark:hover:text-white"
              aria-label={`Remove ${tag}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => addTag(draft)}
          placeholder={tags.length === 0 ? placeholder : 'Add another…'}
          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-gray-900 dark:text-neutral-100 placeholder-gray-400"
        />
      </div>
      {hint && <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">{hint}</p>}
    </div>
  );
}
